import { atom } from 'infrastructure/state/jotai.wrapper'
import {
  CrosswalkMappingSetType,
  FrameworkType,
  StandardAlignmentResponseType,
  LessonAssociation,
  LessonCentricData,
} from 'domains/scope-and-sequence/types'
import {
  getAlignmentsForMappingId,
  getCrosswalkByIds,
} from 'domains/scope-and-sequence/api'
import { ScopeAndSequenceRow } from 'domains/scope-and-sequence/features/parser/types'
import { sheetDataForTable } from 'domains/scope-and-sequence/features/parser/atoms'
import { lessonMappingTreesAtom } from 'domains/scope-and-sequence/features/lesson-mappings/atoms'
import { buildCreateTreePayload } from './convertFromApi'

export const selectedFrameworkAtom = atom<FrameworkType>()

export const commonCoreFrameworkAtom = atom<FrameworkType>()

export const mappingSetsAtom = atom<
  Promise<{
    result: { frameworksSelected: boolean; data?: CrosswalkMappingSetType[] }
    error?: string
  }>
>(async (get) => {
  const selectedFramework = get(selectedFrameworkAtom)
  const ccFramework = get(commonCoreFrameworkAtom)
  if (!selectedFramework || !ccFramework) {
    return { result: { frameworksSelected: false } }
  }
  const result = await getCrosswalkByIds({
    source_id: selectedFramework.id,
    target_id: ccFramework.id,
  })
  return {
    result: {
      frameworksSelected: true,
      data: result.result,
    },
    error: result.error,
  }
})

export const currentMappingSetAtom = atom<CrosswalkMappingSetType>()

export const alignmentsAtom = atom(async (get) => {
  const mappingSet = get(currentMappingSetAtom)
  if (!mappingSet)
    return { error: undefined, result: [] as StandardAlignmentResponseType[] }

  return await getAlignmentsForMappingId(mappingSet.id)
})

const alignmentsBySourceCode = atom(async (get) => {
  const alignments = await get(alignmentsAtom)
  return alignments.result.reduce<
    Record<string, StandardAlignmentResponseType>
  >((acc, alignment) => {
    return {
      ...acc,
      [alignment.source_standard_code]: alignment,
    }
  }, {})
})

export const mappedPacingGuideRowsAtom = atom<
  Promise<
    (ScopeAndSequenceRow & {
      alignment?: StandardAlignmentResponseType
    })[]
  >
>(async (get) => {
  const parsed = get(sheetDataForTable)
  const alignments = await get(alignmentsBySourceCode)

  if (!parsed) return []

  return parsed.map<
    ScopeAndSequenceRow & { alignment?: StandardAlignmentResponseType }
  >((row) => {
    const alignment = alignments[row.standard] ?? alignments[row.standard + '.']
    return { ...row, alignment }
  })
})

export const mappingPacingGuideAtom = atom(async (get) => {
  const mappedRows = await get(mappedPacingGuideRowsAtom)
  return mappedRows.reduce<
    Record<
      string,
      { grade: number; units: { name: string; standards: string[] }[] }
    >
  >((acc, row) => {
    if (!row.alignment) return acc
    if (!(row.grade in acc)) {
      acc[row.grade] = { units: [], grade: row.grade }
    }
    const unitIdx = row.unitNumber - 1
    if (!acc[row.grade].units[unitIdx]) {
      acc[row.grade].units[unitIdx] = {
        name: row.unitName,
        standards: [],
      }
    }
    acc[row.grade].units[unitIdx].standards.push(
      row.alignment.target_standard_code.replace('CCSS.Math.Content.', ''),
    )
    return acc
  }, {})
})

export const treeStructureDataAtom = atom(async (get) => {
  const mappingGuide = await get(mappingPacingGuideAtom)
  const trees = get(lessonMappingTreesAtom)
  if (!trees.length) return undefined
  const [theItem] = Object.values(mappingGuide)
  if (!theItem) return undefined
  return { ...theItem, trees }
})

export const treeStructureFlowIdAtom = atom<string>()
export const treeStructureResultAtom = atom<LessonCentricData>()

// Spreadsheet ID for the exported Google Sheet (for ID editing workflow)
export const exportedSpreadsheetIdAtom = atom<string | undefined>(undefined)

export const treeCreateFlowIdAtom = atom<string>()

export const newTreeTitleAtom = atom('')
export const newTreeAttributionAtom = atom('')

// Imported lesson associations from Google Sheet
export const lessonAssociationsAtom = atom<Record<string, LessonAssociation>>(
  {},
)

export const treeStructureCreateDataAtom = atom((get) => {
  const data = get(treeStructureResultAtom)
  const associations = get(lessonAssociationsAtom)
  const title = get(newTreeTitleAtom)
  if (!data) return

  return buildCreateTreePayload(data, associations, title)
})
